import MuiBreadcrumbs from '@mui/material/Breadcrumbs'
import PropTypes, { shape } from 'prop-types'
import { Menu, MenuItem, SvgIcon, IconButton, Tooltip } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { Box } from '@mui/system'
import { Link } from 'react-router-dom'
import MuiLink from '@mui/material/Link'

import AngleRightIcon from '@hapana/shared/web/styles/assets/svgs/angle-right.svg'
import HapanaAiIcon from '@hapana/shared/web/styles/assets/svgs/hapana-ai-icon.svg'
import { doesPathIncludeBreadcrumbHref } from '@hapana/shared/utils/route.util'
import { commonStyles } from '../../../styles/common.styles'

import Button from '../button/button.component'
import { BUTTON_VARIANTS } from '../button/button.constants'
import PageTitle from '../page-title/page-title.component'

import { styles } from './breadcrumbs.styles'
import {
  CONTEXT_MENU_ANCHOR_POSITION,
  CONTEXT_MENU_ID,
  CORE_PACKAGE,
  MAX_ITEMS,
} from './breadcrumbs.constants'

const Separator = ({ index }) => {
  if (index === 0) {
    return null
  }
  return <SvgIcon component={AngleRightIcon} sx={styles.angleRightSeparator} />
}
Separator.propTypes = {
  index: PropTypes.number,
}
const BreadcrumbLink = ({ label, href, hasRouteIntegration, index, entityName }) => {
  return hasRouteIntegration ? (
    <>
      {entityName && index === 0 && (
        <MuiLink underline="none" sx={styles.entityStyles}>
          {entityName} :
        </MuiLink>
      )}
      <Link to={href} style={styles.breadcrumbLink}>
        {label}
      </Link>
    </>
  ) : (
    <MuiLink href={href} underline="none" sx={styles.breadcrumbLink}>
      {label}
    </MuiLink>
  )
}

BreadcrumbLink.propTypes = {
  label: PropTypes.string,
  href: PropTypes.string,
  hasRouteIntegration: PropTypes.bool,
  index: PropTypes.number,
  entityName: PropTypes.string,
}

const Breadcrumbs = ({
  breadcrumbs,
  context,
  hasRouteIntegration,
  pathname,
  params,
  tabs,
  packageType,
  customStyles = {},
  activeRequests,
  requestHistory = [],
  entityName,
  onRemoveRequest,
  onClearRequestHistory,
  idsLoading = [],
  children,
  isExportEnabled,
  onAiTriggerClick,
  aiTriggerTooltip = 'Hapana Assistant',
  isAiAssistantOpen = false,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  const visibleBreadcrumbs = hasRouteIntegration
    ? breadcrumbs.filter((item) => {
        return item.alwaysVisible || doesPathIncludeBreadcrumbHref(pathname, item.href)
      })
    : breadcrumbs
  const tabName = useMemo(() => {
    if (params?.locationTab) {
      return tabs?.find((tab) => tab.id.toString() === params?.locationTab?.toString())?.label
    }
    return tabs?.find((tab) => tab.id.toString() === params?.tab?.toString())?.label
  }, [params?.locationTab, params?.tab, tabs])
  const pageTitle =
    `${packageType} > ` +
    visibleBreadcrumbs
      .map((item, index) => {
        const separator = index < 1 && packageType === CORE_PACKAGE ? ' : ' : ' > '
        return item.label + (index < visibleBreadcrumbs.length - 1 ? separator : '')
      })
      .join('') +
    (tabName ? ' > ' + tabName : '')

  return (
    <Box sx={[styles.container, customStyles.container]}>
      <PageTitle title={pageTitle} />

      {context ? (
        <Box sx={[styles.contextContainer, customStyles.contextContainer]}>
          <Button
            variant={BUTTON_VARIANTS.TEXT}
            id={CONTEXT_MENU_ID}
            aria-controls={open ? CONTEXT_MENU_ID : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            textPadding={false}
            sx={[styles.contextButton, customStyles.contextButton]}
          >
            {context}:
          </Button>
          <Menu
            id={CONTEXT_MENU_ID}
            aria-labelledby={CONTEXT_MENU_ID}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={CONTEXT_MENU_ANCHOR_POSITION}
          >
            {visibleBreadcrumbs.map((item) => (
              <MenuItem key={item.label}>
                <Link to={item.href} style={styles.breadcrumbLink}>
                  {item.label}
                </Link>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      ) : null}
      <Box sx={styles.breadcrumbsBox}>
        <MuiBreadcrumbs
          sx={[styles.breadcrumbs, customStyles.breadcrumbs]}
          separator={
            packageType === CORE_PACKAGE ? (
              ''
            ) : (
              <SvgIcon component={AngleRightIcon} sx={styles.angleRightSeparator} />
            )
          }
          maxItems={MAX_ITEMS}
          {...props}
        >
          {visibleBreadcrumbs.map(({ label, href }, index) => (
            <Box key={href} sx={commonStyles.verticallyCenteredFlexRow}>
              {packageType === CORE_PACKAGE && <Separator index={index} />}
              <BreadcrumbLink
                label={label}
                href={href}
                hasRouteIntegration={hasRouteIntegration}
                entityName={entityName}
                index={index}
                packageType={packageType}
              />
            </Box>
          ))}
        </MuiBreadcrumbs>
        <PageTitle title={pageTitle} />

        {children}

        {onAiTriggerClick && (
          <Tooltip title={aiTriggerTooltip}>
            <IconButton
              onClick={onAiTriggerClick}
              size="small"
              aria-label={aiTriggerTooltip}
              sx={{
                ...styles.aiTrigger,
                opacity: isAiAssistantOpen ? 0 : 1,
                transition: 'opacity 300ms ease-in-out',
                pointerEvents: isAiAssistantOpen ? 'none' : 'auto',
              }}
            >
              <SvgIcon component={HapanaAiIcon} sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}
Breadcrumbs.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    shape({
      label: PropTypes.string,
      href: PropTypes.string,
    })
  ),
  requestHistory: PropTypes.array,
  activeRequests: PropTypes.array,
  context: PropTypes.string,
  pathname: PropTypes.string,
  hasRouteIntegration: PropTypes.bool,
  entityName: PropTypes.string,
  params: PropTypes.shape({
    tab: PropTypes.string,
    locationTab: PropTypes.string,
  }),

  packageType: PropTypes.string,
  customStyles: PropTypes.object,
  isExportEnabled: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string,
    })
  ),

  children: PropTypes.node,
  idsLoading: PropTypes.array,
  onRemoveRequest: PropTypes.func,
  onClearRequestHistory: PropTypes.func,
  onAiTriggerClick: PropTypes.func,
  aiTriggerTooltip: PropTypes.string,
  isAiAssistantOpen: PropTypes.bool,
}

export default Breadcrumbs
